/**
 * The thirty-two designed screens of the rider application, and which of them this build draws.
 *
 * This list is not navigation chrome. **The design draws almost none** — the app bar carries a
 * `menu` glyph on `1 Where to` and an `arrow_back` on every other screen, and nothing else: no tab
 * bar, no drawer contents, no account menu. The screen switcher at the top of each design file is
 * the design file's own furniture, the same shape the ten console design files use, and building it
 * into the application would be building the catalogue rather than the product.
 *
 * What the list is for is four things that do need one place: the address bar's `?page=`, so a
 * screen is addressable and can be compared against its design; the back arrow knowing where back
 * is; an honest statement of what is not built and why, which is what a screen reached by a stale
 * address is told; and a seam a later agent can add the remaining screens through without editing
 * anything else — see `README.md`'s "Adding the rest".
 *
 * `tests/navigation.test.ts` checks this list against `tests/contract/mobile-screen-support.test.ts`
 * so the two cannot drift apart: that file measures the same ground from the outside, per drawn
 * element, and is not this agent's to edit.
 */

/** What a screen is waiting on, when it is not built. */
export interface ScreenWaiting {
  /** The drawn element with no source, in the design's own words. */
  readonly element: string;
  /** What the platform would have to publish. */
  readonly needs: string;
}

/** Which design file draws a screen. The four the thirty-two are spread across. */
export type RiderDesign =
  | "10 Rider App"
  | "13 WP12 Commerce and Multi-stop"
  | "14 WP13 Money and Account"
  | "07 Payment Outcome Unknown";

export interface RiderScreen {
  /** The `?page=` identifier. Lowercase and hyphens, which is also what the router matches on. */
  readonly id: string;
  /** The design file's own name for the screen, as `SCREEN_SPEC.md` heads it. */
  readonly title: string;
  readonly design: RiderDesign;
  /** The operation this build reads it from, or null when nothing published serves its subject. */
  readonly operation: string | null;
  readonly built: boolean;
  /**
   * Why this screen is **not** built. Empty when it is.
   *
   * `tests/navigation.test.ts` asserts that, which is what keeps the two fields from drifting into
   * one: a screen cannot be both drawn and waiting.
   */
  readonly waiting: readonly ScreenWaiting[];
  /**
   * Drawn elements this screen renders as the designed absence, because nothing publishes them.
   *
   * The other half of `waiting`, and the half a built screen has. The measurement grades a screen
   * `degraded` when an element it calls **non-essential** has no source — the screen can be built
   * and loses something drawn — and this is where that loss is recorded per screen rather than
   * being visible only as a dashed pill on a phone. `tests/navigation.test.ts` counts these against
   * the measurement's own `missing` list, so a gap that closes upstream fails the test here.
   */
  readonly absent: readonly ScreenWaiting[];
}

const RIDE_JOURNEY =
  "GET /v1/tenants/{tenantId}/customer-app/customers/{customerId}/ride-journeys/{rideRequestId} (EXP-002)";
const CART_REVIEW =
  "GET /v1/tenants/{tenantId}/customer-app/customers/{customerId}/cart-reviews/{cartId} (EXP-002)";
const PAYMENT_OUTCOME =
  "GET /v1/tenants/{tenantId}/customer-app/customers/{customerId}/payment-outcomes/{executionId} (EXP-002)";
const ORDER_HISTORY =
  "GET /v1/me/trips (TRP-01), GET /v1/me/commerce-orders (COM-05) and " +
  "GET /v1/me/delivery-orders (DEL-03)";
const WHERE_TO = "POST /v1/reverse-geocode (GEO-02) and POST /v1/geocode (GEO-01)";
const CHOOSE =
  "GET /v1/ride-products (MOB-01), POST /v1/distance-eta (GEO-04) and POST /v1/ride-quotes (MOB-02)";
const VALIDATE_QUOTE = "POST /v1/ride-quotes/{id}/validate (MOB-02)";
const DISPATCH_PROCESS =
  "GET /v1/tenants/{tenantId}/dispatch-processes/{demandId} (DSP-04), with GET /v1/ride-requests/{id} (MOB-03) for the version its cancel needs";

/**
 * All thirty-two, in the order `tests/contract/mobile-screen-support.test.ts` measures them.
 *
 * The first nine built were the booking flow less `5 Finding` and `5b No driver`, which were
 * **blocked** and deliberately absent: DSP-04 published its process progress only inside the 202 of
 * an event-ingestion `POST`, behind `dispatch:orchestrate`, and there was no `GET` at all — so the
 * search state had no source and a screen built over it would have been a spinner with an invented
 * countdown on it.
 *
 * Thirteen more were added in a second tranche, which was every remaining screen the measurement
 * graded `buildable` or `degraded`: the commerce set less `Checkout` and the two multi-stop screens,
 * `Refund`, `Consent`, `Settings`, and all five of `07 Payment Outcome Unknown`.
 *
 * The two search screens are the third tranche, and they are the cheapest change this list has
 * recorded. [ADR 0031](../../../docs/adr/0031-the-dispatch-process-is-read-under-its-own-scope-and-bounded-by-the-tenant-because-the-record-has-no-subject.md)
 * served `GET /v1/tenants/{tenantId}/dispatch-processes/{demandId}` under its own `dispatch:read`,
 * and **every field those two screens draw was already in DSP-04's row** — so one read moved both
 * from `blocked` to `buildable` with nothing missing, and nothing had to be invented to build them.
 * The reason it was that cheap is that the absence had been recorded accurately rather than worked
 * around: a refusal that says what was missing is worth more than a screen that renders a guess.
 *
 * `Multi-stop in trip` is the fourth tranche and is one screen, for the same reason the third was
 * two: ADR 0036 made a stop name a GEO-02 address and closed the one element it was blocked on, so
 * the place is a second read away from a plan rather than a field nobody publishes. It cost no new
 * operation and no new route — two scopes this application had never spent, and a component.
 *
 * The money screens are the fifth tranche and are the most expensive three on this list, because
 * all three were blocked on things the platform did not have rather than on routes it had put in
 * the wrong place. [ADR 0041](../../../docs/adr/0041-a-riders-own-wallet-and-the-balance-projection-behind-it-are-published-under-v1.md)
 * published FIN-01’s wallet read, its top-up command and FIN-03’s balance projection under `/v1`
 * — adding `authoritativeAsOf` from a checkpoint FIN-03 already kept, with no column and no
 * migration — and [ADR 0042](../../../docs/adr/0042-a-masked-mobile-money-instrument-is-pay-04s-record-and-only-its-mask-is-published.md)
 * gave PAY-04 a stored mobile-money instrument and published only its mask. `Wallet` and
 * `Wallet not authoritative` are **one component**, because the design says they are one screen in
 * two states. Twenty-eight are drawn.
 *
 * The four that are not are **blocked** and each says what it is waiting on. They are listed in
 * full so that the application can answer for a screen it cannot draw, and so that adding one is an
 * edit to this array and a branch in `main.tsx` rather than an archaeology exercise.
 */
export const riderScreens: readonly RiderScreen[] = [
  {
    id: "where-to",
    title: "1 Where to",
    design: "10 Rider App",
    operation: WHERE_TO,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Home · Kilimani` and `Work · Upper Hill` — the rider's saved places",
        needs: "a GET listing for /v1/customers/{customerId}/saved-places; only the POST exists",
      },
    ],
  },
  {
    id: "choose",
    title: "2 Choose",
    design: "10 Rider App",
    operation: CHOOSE,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`3 min` — how long until a driver of that class could reach the rider",
        needs: "result.pickupEtaSeconds, products.pickupEtaSeconds or availability.etaSeconds",
      },
    ],
  },
  {
    id: "quote",
    title: "3 Quote",
    design: "10 Rider App",
    operation: VALIDATE_QUOTE,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`M-Pesa ··· 0421` — a stored, masked payment instrument",
        needs:
          "nothing — PAY-04 publishes instruments.maskedAccount since ADR 0042, and this built screen still draws the absence. A fidelity defect this tranche created rather than closed: R-54",
      },
      {
        element: "`Cancel free · Within 2 min` — the free-cancellation window",
        needs:
          "quote.freeCancellationUntil, request.freeCancellationUntil or trip.freeCancellationUntil",
      },
      {
        element: "`Surge · None after booking` — the pricing policy in force",
        needs: "a policy read that does not require a policyId nobody holds (PRC-03)",
      },
    ],
  },
  {
    id: "quote-expired",
    title: "3b Quote expired",
    design: "10 Rider App",
    operation: VALIDATE_QUOTE,
    built: true,
    waiting: [],
    absent: [],
  },
  {
    id: "confirming",
    title: "4 Confirming",
    design: "10 Rider App",
    operation: RIDE_JOURNEY,
    built: true,
    waiting: [],
    absent: [],
  },
  {
    id: "finding",
    title: "5 Finding",
    design: "10 Rider App",
    operation: DISPATCH_PROCESS,
    built: true,
    waiting: [],
    absent: [],
  },
  {
    id: "no-driver",
    title: "5b No driver",
    design: "10 Rider App",
    operation: `${DISPATCH_PROCESS}, with MOB-01's catalogue and MOB-02's price for the class it offers instead`,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`4 min` on the alternative class row — when a driver of it could arrive",
        needs:
          "result.pickupEtaSeconds, products.pickupEtaSeconds or availability.etaSeconds; the same absence `2 Choose` draws three of",
      },
      {
        element: "`Schedule for later` — booking the ride for another time",
        needs:
          "a time on MOB-03's request body; POST /v1/ride-requests takes a quoteId and nothing else",
      },
    ],
  },
  {
    id: "driver-coming",
    title: "6 Driver coming",
    design: "10 Rider App",
    operation: RIDE_JOURNEY,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`KDA 417Q` — the plate of the vehicle coming",
        needs:
          "driverProfile.vehicleId, trip.vehicleId, assignments.vehicleId or availability.vehicleId",
      },
      {
        element: "`Toyota Axio · silver` — the vehicle's make, model and colour",
        needs: "vehicle.make, vehicle.model and vehicle.colour",
      },
      {
        element: "`chat` — a message thread with the driver",
        needs: "thread.messages, messages.body or conversation.messages",
      },
    ],
  },
  {
    id: "in-trip",
    title: "7 In trip",
    design: "10 Rider App",
    operation: RIDE_JOURNEY,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Your trip is shared with Mama (live link)` — who the trip is shared with",
        needs: "incident.contactAliases, sharing.contactAliases or trip.sharing",
      },
      {
        element: "the driver's position arriving without being asked for",
        needs: "a transport that delivers trip location unasked to this application (RTM-001)",
      },
    ],
  },
  {
    id: "complete",
    title: "8 Complete",
    design: "10 Rider App",
    operation: RIDE_JOURNEY,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Paid · M-Pesa ··· 0421 · confirmed` — which instrument paid",
        needs:
          "nothing — PAY-04 publishes instruments.maskedAccount since ADR 0042, and this built screen still draws the absence. A fidelity defect this tranche created rather than closed: R-54",
      },
    ],
  },
  {
    id: "fare-differs",
    title: "8b Fare differs",
    design: "10 Rider App",
    operation: RIDE_JOURNEY,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Question this fare` — opening a case about it",
        needs:
          "a public support-case write; only POST /internal/v1/tenants/{tenantId}/support-cases exists",
      },
    ],
  },

  /* -------------------------------------------------------------------- the second tranche ---- */

  {
    id: "merchants",
    title: "Merchants",
    design: "13 WP12 Commerce and Multi-stop",
    operation: "GET /v1/merchants (MER-01)",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`KES 99 delivery` — a per-merchant delivery fee",
        needs:
          "merchant.deliveryFeeMinor, merchantLocation.deliveryFeeMinor or quote.deliveryFeeMinor",
      },
      {
        element: "`4.8` — a merchant rating",
        needs: "merchant.ratingBasisPoints or merchant.averageScoreBasisPoints",
      },
      {
        element: "`Opens 14:00` — opening hours, and the `Open now`/`Opening later` split",
        needs: "merchantLocation.openingHours or merchantLocation.hours",
      },
      {
        element: "`Grocery · 20–30 min` — the category and the preparation time",
        needs:
          "a listing of a merchant's branches; MER-02 publishes preparationMinutes and no way to reach a locationId from a merchant",
      },
      {
        element: "`Deliver to Kilimani` — the saved place being shopped for",
        needs: "a GET listing for /v1/customers/{customerId}/saved-places; only the POST exists",
      },
    ],
  },
  {
    id: "products",
    title: "Products · stock",
    design: "13 WP12 Commerce and Multi-stop",
    operation:
      "GET /v1/tenants/{tenantId}/products (COM-02), GET /v1/tenants/{tenantId}/catalogs/{catalogId} (COM-01), GET /v1/merchants/{merchantId}/locations/{locationId}/inventory/{sku} (COM-03) and MER-01/MER-02 for the app bar",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`usually in` — the fallback half of the `stock not reported` row",
        needs:
          "a freshness or fallback field on COM-03's availability (G-36, still open); DRV-05's fresh is the pattern",
      },
      {
        element: "`each` / `bunch` / `tray` — a unit of measure",
        needs: "a unit field on COM-02's product; its description is drawn in that slot meanwhile",
      },
    ],
  },
  {
    id: "cart",
    title: "Cart",
    design: "13 WP12 Commerce and Multi-stop",
    operation: CART_REVIEW,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Delivery KES 99` and `Service KES 20` — the two fees in the total",
        needs: "cart.deliveryFeeMinor, cart.serviceFeeMinor or cart.totalAmountMinor",
      },
      {
        element: "`Total KES 559` and `Checkout · KES 559` — what the rider will be charged",
        needs: "the same two fees; EXP-002 answers chargeableTotalAbsence for exactly this",
      },
    ],
  },
  {
    id: "cart-conflict",
    title: "Cart conflict",
    design: "13 WP12 Commerce and Multi-stop",
    operation: `${CART_REVIEW} and PUT /v1/carts/{cartId}/items (COM-04), whose 409 is this screen`,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`added on your tablet` — which device made each change",
        needs: "cart.items.addedByDeviceId or cart.items.source",
      },
      {
        element: "`Remove tablet changes` — undoing one device's half",
        needs: "the same per-item provenance; without it nothing says which changes to remove",
      },
    ],
  },
  {
    id: "checkout",
    title: "Checkout",
    design: "13 WP12 Commerce and Multi-stop",
    /*
     * Two reads and the one command, which is the whole of why this screen moved.
     *
     * `waiting` was three entries and is now none. The first of them — a defined body for the order
     * command — was not a record nothing held: the contract referenced
     * `#/components/schemas/PlaceOrder` and never defined it, so the command the commerce flow
     * exists to reach described nothing a caller could send. The branch was a module-local `const`
     * inside a `z.discriminatedUnion`; exporting it published the schema by derivation.
     *
     * The other two moved from `waiting` to `absent` rather than being closed, and that is the
     * distinction those two fields carry: a screen is not drawn because of `waiting`, and a drawn
     * screen loses a cell because of `absent`. Neither was ever a reason not to draw this screen —
     * an address and a price hold are two cells on a page whose subject is the order.
     */
    operation: `${CART_REVIEW}, GET /v1/tenants/{tenantId}/mobile-money/instruments (PAY-04) and POST /v1/tenants/{tenantId}/commerce-orders (COM-05), which is the one command`,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Deliver to Kilimani, Rose Ave 12` — the chosen saved place",
        needs: "a GET listing for /v1/customers/{customerId}/saved-places; only the POST exists",
      },
      {
        element: "`price held 9:48` — a hold on the checkout price",
        needs: "order.priceHeldUntil or cart.priceHeldUntil",
      },
      {
        /*
         * The one absence on this screen that is a refusal rather than a silence.
         *
         * `Cart` records the same two fees as unpublished. Here it is stronger: COM-05 rejects any
         * order whose `totalAmountMinor` is not the line snapshot subtotal, so the fee-inclusive
         * figure is not merely unpublished — the command would answer 400 to it. The screen draws
         * the subtotal, which is what will actually be charged, and says why it differs.
         */
        element: "`Pay KES 559` and `Place order · KES 559` — the fee-inclusive total",
        needs:
          "cart.deliveryFeeMinor and cart.serviceFeeMinor; and COM-05 would refuse their sum even if they existed, because it requires the total to equal the lines",
      },
      {
        element: "`Card ··· 4417` / `Visa` — a stored card to pay with",
        needs: "a card instrument record; PAY-04 models M-Pesa and Airtel Money only",
      },
      {
        element: "`Cash on delivery` being the chosen method",
        needs: "a payment method on an order; COM-05 records none",
      },
    ],
  },
  {
    id: "order-tracking",
    title: "Order tracking",
    design: "13 WP12 Commerce and Multi-stop",
    operation: "GET /v1/order-preparations/{orderId} (COM-07)",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Sukuma wiki · 2 → 1 bunch` — what the store changed",
        needs: "acceptance.substitutions or orderPreparation.substitutions",
      },
      {
        element: "the store's progress arriving without being asked for",
        needs:
          "a transport that delivers order preparation progress unasked to this application (RTM-001); this screen deliberately does not poll instead",
      },
      {
        element: "`Refund KES 30 · to M-Pesa` — the refund for what was not packed",
        needs:
          "a paymentIntentId on COM-07's preparation, or a FIN-04 filter by order; nothing joins an order to the money taken for it",
      },
      {
        element: "`Message the store` — the thread with the merchant",
        needs: "a published message thread between a customer and a merchant",
      },
    ],
  },
  {
    id: "multi-stop",
    title: "Multi-stop",
    design: "13 WP12 Commerce and Multi-stop",
    /*
     * **Re-measured 18 October: two blockers, not three.**
     *
     * The first entry below used to ask for `plan.stops.addressLabel` or a sibling, and that is
     * not a blocker and has not been one since ADR 0036. A stop names a GEO-02 address by
     * identifier, and `MultiStopInTrip.tsx` already turns those into place names on the screen
     * beside this one — `plan.stops[].addressId` to `GET /v1/addresses/{addressId}`, once per
     * distinct address, with `tests/integration/stop-address-path.test.ts` assembling the path
     * over real sockets. The same read works here for the same reason.
     *
     * It is removed from the list below rather than left in it, because the list is what is
     * blocking and this is not. It is written out here instead, because a blocker that silently
     * disappears teaches nobody: the next reader should see that a third of this screen's problem
     * already has an answer, and which answer it is.
     *
     * The two that remain are both domain questions rather than fields. Nothing prices a stop
     * plan — TRP-04's `Stop` carries `stopId`, `sequence`, `addressId`, `instructions`, `status`
     * and two timestamps, and the plan carries no money at all — so `New estimate KES 2,640`
     * needs somebody to decide what prices a multi-stop trip before it needs a column. And
     * nothing plans a wait, so `wait 5 min` is a concept the domain does not have rather than a
     * field it does not publish.
     */
    operation: null,
    built: false,
    waiting: [
      {
        element: "`New estimate KES 2,640` against `Was KES 2,480 before reorder`",
        needs:
          "a price for a stop plan. TRP-04 publishes no money on the plan or on a stop, and what " +
          "prices a multi-stop trip — and what makes a reorder cost more — is a pricing decision " +
          "before it is a field",
      },
      {
        element: "`wait 5 min` — the dwell planned at a stop",
        needs:
          "a planned wait on a stop. TRP-04 records `arrivedAt` and `completedAt`, which is how " +
          "long a stop took, not how long it was meant to take",
      },
    ],
    absent: [],
  },
  {
    id: "multi-stop-in-trip",
    title: "Multi-stop in trip",
    design: "13 WP12 Commerce and Multi-stop",
    /*
     * Drawn, and the two-hop read is why it could be.
     *
     * `Heading to Upper Hill` was this screen's only absence and it waited on
     * `plan.stops.addressLabel` — a field name Q-53 named as the one not to invent. ADR 0035 gave
     * the platform an address GEO-02 owns and ADR 0036 made a stop name one, so the place comes from
     * two published operations: the plan read hands over `plan.stops.addressId` and
     * `GET /v1/addresses/{addressId}` answers `address.formattedAddress`. `waiting` is therefore
     * empty rather than restated, because nothing is missing.
     *
     * This list carried it for one tranche as buildable-and-not-drawn, named in
     * `tests/navigation.test.ts` with the record that explained it, because the measurement had
     * moved and no pixel had. That exemption is gone rather than loosened: the screen is built and
     * the test's derived assertion stands on its own again. R-49 is the record.
     *
     * The two absences below were found by **building** it rather than by the sweep, which is the
     * same thing that happened on `Merchants`: the measurement compares drawn elements against
     * fields, and a build also discovers what cannot be composed from the fields that exist.
     */
    operation:
      "GET /v1/tenants/{tenantId}/stop-plans/{planId} (TRP-04), GET /v1/addresses/{addressId} (GEO-02), POST /v1/distance-eta (GEO-04) and POST /v1/tenants/{tenantId}/trips/{tripId}/safety-incidents (SAF-01)",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`CityCab · XL` — the vehicle class in the app bar",
        needs:
          "a product, class or vehicle on TRP-04's plan; it publishes tripId, tripVersion, riderId, driverId, version and its stops, and none of those names a vehicle. The same absence is why GEO-04's required `mode` is this build's value",
      },
      {
        element: "`arrive about 13:40` — when the trip reaches its last stop",
        needs:
          "plan.stops.waitSeconds or plan.stops.dwellSeconds; an arrival over the remaining stops is the legs plus the dwell at each one, and summing the legs alone draws a time a rider arrives after",
      },
    ],
  },
  {
    id: "payment-methods",
    title: "Payment methods",
    design: "14 WP13 Money and Account",
    /*
     * Drawn, and the record it draws had to be designed before it could be read.
     *
     * This screen was blocked on the oldest finding in the measurement — "six screens draw
     * `M-Pesa ··· 0421`; no service models one" — and
     * [ADR 0042](../../../docs/adr/0042-a-masked-mobile-money-instrument-is-pay-04s-record-and-only-its-mask-is-published.md)
     * made PAY-04 the owner of a registered mobile-money account and published **only its mask**.
     * The listing is bounded by the record's own `owner_subject` against the verified token, so it
     * is the platform's third subject-bounded listing with no BFF in front of it.
     *
     * The four absences below were all found by **building** it rather than by the sweep, which now
     * grades this screen whole. Three of them are one finding said three ways: the design draws the
     * two primary actions of this tranche and no form for either.
     */
    operation: "GET /v1/tenants/{tenantId}/mobile-money/instruments (PAY-04)",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Add mobile money` and the app bar's `add` — entering a number",
        needs:
          "a designed form. POST /v1/tenants/{tenantId}/mobile-money/instruments exists and takes a provider and an account reference; 14 WP13 Money and Account.dc.html draws no number entry anywhere, so this build has nothing to send and the button says so. Q-144",
      },
      {
        element: "`●` and `○` on each row — choosing which instrument is the default",
        needs:
          "a command to move the default. ADR 0042 derives it from being the first registered rather than publishing one. Q-144",
      },
      {
        element: "`Visa ··· 4417` with `Expires 08/28` — a stored card",
        needs:
          "a stored card instrument. PAY-05's three operations are card authorizations under /internal/v1 and nothing publishes a card's mask or expiry, so the row is drawn as the absence",
      },
      {
        element: "`set up` on `Bank transfer`",
        needs:
          "a command that registers a bank instrument. PAY-06 publishes two bank-rail executions under /internal/v1 and nothing else",
      },
    ],
  },
  {
    id: "wallet",
    title: "Wallet",
    design: "14 WP13 Money and Account",
    /*
     * Drawn, over three reads across three services, two of which
     * [ADR 0041](../../../docs/adr/0041-a-riders-own-wallet-and-the-balance-projection-behind-it-are-published-under-v1.md)
     * published.
     *
     * The design's own note says this screen draws "FIN-01 balance with the FIN-03 authoritative
     * flag", and **FIN-01 has no balance** — its contract says balances and postings are deliberately
     * absent, and AGENTS.md is why. So the figure is FIN-03's projection of FIN-02's ledger, FIN-01
     * supplies the currency it is written in and whether the wallet can be spent from, and
     * `Wallet.tsx` records the correction where a reader of the design would look for it.
     */
    operation:
      "GET /v1/wallets/{walletId} (FIN-01), GET /v1/tenants/{tenantId}/accounts/{accountId}/balances (FIN-03) and GET /v1/journal-transactions (FIN-02)",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Top up` — adding money",
        needs:
          "a designed form. POST /v1/wallets/{walletId}/top-ups exists and takes an amount and a currency; 14 WP13 Money and Account.dc.html draws no amount entry anywhere, so this build has nothing to send and the button says so. Q-148",
      },
      {
        element: "`Trip trp_8F2K` and `Top-up · M-Pesa` — what each movement was for",
        needs:
          "a trip, an order or a provider on a journal transaction. FIN-02 publishes sourceType and sourceId and nothing joins either to a trip, an order or a mobile-money provider, so the row is drawn as the ledger names it",
      },
      {
        element: "`processing` on the refund movement",
        needs:
          "a refund's own status beside a posting. FIN-02 records posted transactions only, so a posting that exists has happened; the status is FIN-04's and this screen does not read FIN-04",
      },
      {
        element: "`CityCab` — the tenant's trading name in the app bar",
        needs:
          "a tenant trading name published to this application. The token carries sub, name, given_name, scope and tenant_id, and TEN-05's branding is read by the consoles",
      },
      {
        element: "this rider's own wallet and the ledger account behind it",
        needs:
          "a listing of a customer's wallets, and anything that joins a wallet to the account FIN-02 posts to. Neither exists, so both identifiers arrive in the address as ?wallet= and ?account= — and the second is what stops FIN-03's public read from being bounded to a person at all. Q-145",
      },
      {
        element: "this rider's own movements rather than the tenant's",
        needs:
          "an account or customer filter on GET /v1/journal-transactions. It offers sourceType, currency, limit and after, so the page that arrives holds every posting in the tenant and this screen narrows it by lines.accountId. Q-145",
      },
    ],
  },
  {
    id: "wallet-not-authoritative",
    title: "Wallet not authoritative",
    design: "14 WP13 Money and Account",
    /*
     * The same component as `wallet`, because the design says these are one screen in two states:
     * "When not authoritative, it reads 'About' with a dashed chip and the uncounted item."
     *
     * Which one is drawn is decided by the data and the address follows, exactly as `Cart` and
     * `Cart conflict` already do here. FIN-03 answers `authoritative: false` unconditionally and
     * cannot do otherwise, so the judgement is the screen's: a FIN-02 posting whose sequence is past
     * FIN-03's checkpoint is one the projection has not absorbed, and `authoritativeAsOf` — which
     * ADR 0041 published from the tenant checkpoint this service already kept — is when it last had
     * everything.
     *
     * The absences are `wallet`'s, because it is `wallet`'s component.
     */
    operation:
      "GET /v1/wallets/{walletId} (FIN-01), GET /v1/tenants/{tenantId}/accounts/{accountId}/balances (FIN-03) and GET /v1/journal-transactions (FIN-02)",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Top up` — adding money",
        needs:
          "a designed form. POST /v1/wallets/{walletId}/top-ups exists and takes an amount and a currency; the design draws no amount entry anywhere. Q-148",
      },
      {
        element: "`Order co_9A1B` — what the uncounted movement was for",
        needs:
          "an order on a journal transaction. FIN-02 publishes sourceType and sourceId and nothing joins either to an order, so the row is drawn as the ledger names it",
      },
      {
        element: "`CityCab` — the tenant's trading name in the app bar",
        needs: "a tenant trading name published to this application",
      },
      {
        element: "this rider's own wallet and the ledger account behind it",
        needs:
          "a listing of a customer's wallets, and anything that joins a wallet to the account FIN-02 posts to. Q-145",
      },
      {
        element: "this rider's own movements rather than the tenant's",
        needs: "an account or customer filter on GET /v1/journal-transactions. Q-145",
      },
    ],
  },
  {
    id: "refund",
    title: "Refund",
    design: "14 WP13 Money and Account",
    operation: "GET /v1/refunds/{refundId} (FIN-04)",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Refund requested · 12:47 · by the store` — the three timeline rows",
        needs: "refund.timeline, events.occurredAt or refund.history",
      },
      {
        element: "`Order co_7Q2K` — the order the refund is against",
        needs: "an order reference on FIN-04's refund; it publishes the paymentIntentId",
      },
      {
        element: "this customer's refunds, so one could be opened from a list",
        needs:
          "a payer or customer filter on GET /v1/refunds; today it answers every refund in the tenant, so this screen is addressed by refundId instead",
      },
    ],
  },
  {
    id: "order-history",
    title: "Order history",
    design: "14 WP13 Money and Account",
    /*
     * **Built on 18 October, and the blocking element was closed by a decision rather than a
     * route.**
     *
     * This screen waited on one thing: where a trip's fare and its two addresses live. The entry
     * that stood here measured the alternative and rejected it — MOB-03's request and MOB-02's
     * quote are both public and both by identifier, so a fan-out worked and cost two calls per
     * row, four hundred for a full page, and neither read takes a version while TRP-01 pins
     * `quoteVersion` deliberately. A fan-out would have drawn each quote as it is now rather than
     * what the rider was charged, which is the one thing a history may not do.
     *
     * The fare and the labels are recorded on the trip itself now, so the screen reads one
     * listing per domain. Three listings and no component composing them, which ADR 0097 settled
     * deliberately: a BFF exists for the read a channel cannot make, and three parallel reads are
     * a read a phone makes perfectly well. The screen reports each source on its own instead, so
     * one being down draws a note naming it rather than a history quietly short a few rows.
     */
    operation: ORDER_HISTORY,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Kilimani Fresh` — the shop a basket order was placed with",
        needs:
          "a merchant name on COM-05's own listing. It publishes merchantId; MER-01 holds the " +
          "name, and a listing cannot make one call per row to resolve it",
      },
      {
        element: "`Parcel to Oyster Bay` — where a parcel was going",
        needs:
          "an address label on DEL-03's listing. It publishes deliveryAddressId, and resolving " +
          "an identifier to a place name is a second read per row",
      },
      {
        element: "`TZS 8,000` — what a parcel cost",
        needs:
          "any money at all on DEL-03's delivery order. It publishes no total and no currency, " +
          "so the row is drawn with the amount absent rather than appearing to have been free",
      },
      {
        element: "`retry scheduled` — that a failed delivery will be attempted again",
        needs:
          "a retry plan on DEL-03. Its vocabulary ends at `failed`, which says what happened and " +
          "not what happens next",
      },
      {
        element: "`cancelled · free` — that cancelling this trip cost nothing",
        needs:
          "a cancellation fee on TRP-01. It records that a trip was cancelled and not what " +
          "cancelling it cost, so the word is drawn and the fee is not asserted",
      },
      {
        element: "`Tap any item for its receipt.` — the receipt behind a row",
        needs:
          "a receipt screen, which this deliverable does not design, and an operation returning " +
          "one for a commerce order or a parcel. The copy is drawn word for word and the rows " +
          "carry no control, with the gap flagged on screen",
      },
      {
        element: "the `filter_list` control in the bar",
        needs:
          "a filter on any of the three listings. All three take a window and a limit; none " +
          "takes a status or a kind, so the control is drawn and says why it cannot work",
      },
    ],
  },
  {
    id: "support",
    title: "Support",
    design: "14 WP13 Money and Account",
    /*
     * Two of four closed, and **neither read was ever widened to close them**.
     *
     * SUP-01's `assertCustomerOrAgent` has refused a caller who is neither the case's customer
     * nor an agent since the service was written. SUP-02 goes further and is per entry:
     * `TimelineService.list` refuses a subject on no entry's `participantSubjects`. Both reads
     * were on `/internal/v1`, which the gateway does not expose, so a rider could not reach a
     * case that was already their own to read. ADR 0078 publishes the `/v1` siblings and a
     * listing — which is what makes this a screen rather than a destination, since nothing
     * answered which cases are mine — and changes no authorization at all.
     *
     * **It is still not built, and the reason is a decision rather than a route.** A customer
     * reply is not a sensitive note: that table is agent-only and SUP-02 deliberately does not
     * project it, which is what makes it sensitive. A customer message needs its own record, its
     * own event for the timeline to project, and an answer to whether saying something moves the
     * case to `pending_support`. Published reads without it would be a screen that shows a
     * conversation a rider cannot join.
     */
    operation: null,
    built: false,
    waiting: [
      {
        element: "`Reply` — adding to the thread",
        needs:
          "an operation for a customer to add to a case, with a record of its own: SUP-01 publishes a create, a transition and a sensitive note, and the note is agent-only and deliberately unprojected",
      },
      {
        element: "an agent's reply arriving without being asked for",
        needs: "a transport that delivers support case replies unasked (RTM-001)",
      },
    ],
    absent: [],
  },
  {
    id: "consent",
    title: "Consent",
    design: "14 WP13 Money and Account",
    operation:
      "GET /v1/tenants/{tenantId}/customers/{customerId}/consents (CUS-04), with its grant and its withdrawal",
    built: true,
    waiting: [],
    absent: [
      {
        element:
          "`Needed to find drivers` and the three sentences beside it — what each purpose means",
        needs: "a description on CUS-04's purpose; its purpose is a free string with no vocabulary",
      },
      {
        element: "a glyph per purpose — `location_on`, `share_location`, `campaign`, `insights`",
        needs:
          "an enumerated purpose, or an icon field; every row draws `lock` meanwhile (Q-80's rule)",
      },
      {
        element: "`See consent history` — the history as its own screen",
        needs: "a design file; the bundle draws thirty-two rider screens and no consent history",
      },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    design: "14 WP13 Money and Account",
    operation:
      "GET /v1/customers/{customerId}/preferences (CUS-03) and GET /v1/sessions?subjectId= (IAM-04)",
    built: true,
    waiting: [],
    absent: [
      {
        element: "`account active` — the account's own standing",
        needs:
          "a customer read addressed by customerId; CUS-01's profile read takes a profileId nothing joins to one",
      },
      {
        element: "`Trips, orders, offers` — which topics a rider is told about",
        needs:
          "a public NTF-06; CUS-03 publishes the three communication channels and not the topics, so the channels are drawn",
      },
      {
        element: "the four screens the `›` chevrons lead to",
        needs: "a design file; CUS-03's PUT is published and no screen for it is drawn",
      },
    ],
  },
  {
    id: "approve-on-phone",
    title: "1 · Approve on phone",
    design: "07 Payment Outcome Unknown",
    operation: PAYMENT_OUTCOME,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Mobile money ··· 0421` — the masked instrument, drawn on all five screens",
        needs:
          "a service that models a stored payment instrument; EXP-002 answers instrumentAbsence",
      },
      {
        element: "`Your trip is finished and nothing else is waiting on this payment.`",
        needs: "a trip state beside the payment's reference; EXP-002 publishes the reference alone",
      },
    ],
  },
  {
    id: "outcome-unknown",
    title: "2 · Outcome unknown",
    design: "07 Payment Outcome Unknown",
    operation: PAYMENT_OUTCOME,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Asked your provider 3 times · last at 21:15:40`",
        needs: "attemptCount, providerAttemptCount or lastAskedAt",
      },
      {
        element: "`Get help` — opening a case from here",
        needs:
          "a public support-case write; only POST /internal/v1/tenants/{tenantId}/support-cases exists",
      },
      {
        element: "`You'll get a notification the moment we know`",
        needs: "a transport that delivers the payment outcome unasked (RTM-001)",
      },
      {
        element: "`Mobile money ··· 0421` and `Your trip is complete.`",
        needs: "the same two as `1 · Approve on phone`",
      },
    ],
  },
  {
    id: "still-unknown",
    title: "3 · Still unknown",
    design: "07 Payment Outcome Unknown",
    operation: PAYMENT_OUTCOME,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Asked your provider 31 times · last at 21:29:10`",
        needs: "attemptCount, providerAttemptCount or lastAskedAt",
      },
      {
        element: "`CityCab support has been told and is checking`",
        needs: "a public support-case read; only the /internal/v1 one exists",
      },
      {
        element: "`Chat with support`",
        needs: "a POST /v1/tenants/{tenantId}/support-cases/{id}/messages",
      },
      {
        element: "`Mobile money ··· 0421`",
        needs: "a service that models a stored payment instrument",
      },
    ],
  },
  {
    id: "confirmed-paid",
    title: "4a · Confirmed paid",
    design: "07 Payment Outcome Unknown",
    operation: PAYMENT_OUTCOME,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`mobile money ··· 0421` — which instrument paid",
        needs:
          "nothing — PAY-04 publishes instruments.maskedAccount since ADR 0042, and this built screen still draws the absence. A fidelity defect this tranche created rather than closed: R-54",
      },
      {
        element: "`Est. KES 980 · Final KES 980` — the estimate beside the final",
        needs: "a join from a payment to the MOB-02 quote the ride was priced at",
      },
      {
        element: "`View receipt` — the receipt itself",
        needs: "a published receipt; PAY-02 answers an execution and no document",
      },
    ],
  },
  {
    id: "confirmed-not-charged",
    title: "4b · Confirmed not charged",
    design: "07 Payment Outcome Unknown",
    operation: PAYMENT_OUTCOME,
    built: true,
    waiting: [],
    absent: [
      {
        element: "`Pay by card` — choosing another instrument",
        needs:
          "nothing — PAY-04 publishes instruments.maskedAccount since ADR 0042, and this built screen still draws the absence. A fidelity defect this tranche created rather than closed: R-54",
      },
      {
        element: "`Try mobile money again` — the one safe retry the design draws",
        needs:
          "an intentId beside the outcome; POST /v1/payment-executions requires one and EXP-002 publishes the intent's status alone",
      },
    ],
  },
];

/** The screens this build draws, which is what the router has a branch for. */
export const builtScreens: readonly RiderScreen[] = riderScreens.filter((screen) => screen.built);

export function screenById(id: string): RiderScreen | undefined {
  return riderScreens.find((screen) => screen.id === id);
}

/**
 * Where the app bar's back arrow goes from each built screen.
 *
 * The design draws the control and never says where it leads, which is a thing the design is silent
 * about rather than ambiguous about, so this is a recorded assumption rather than a blocking
 * question. The order is the flow's own: a rider steps back through the screens they came forward
 * through, and the three screens after a ride exists go back to where a ride is chosen rather than
 * into a trip that has already happened.
 *
 * `1 Where to` has no back arrow — the design gives it `menu` instead — so it is absent here. So
 * does `Merchants`, which `13 WP12 Commerce and Multi-stop.dc.html` also gives `menu`: the two front
 * doors of the two things this application does.
 *
 * The second tranche's twelve follow the same rule and the commerce ones form a chain the design's
 * own screen order implies — merchants, products, cart, checkout, tracking. The four payment-outcome
 * screens go back to `1 Where to` rather than into each other: they are reached from a notification
 * or from a trip rather than from one another, and a rider on `4a · Confirmed paid` stepping back to
 * `2 · Outcome unknown` would be stepping into a state that is no longer true.
 */
export const backFrom: Readonly<Record<string, string>> = {
  choose: "where-to",
  quote: "choose",
  "quote-expired": "choose",
  confirming: "quote",
  /*
   * The two search screens, which follow the same rule as the three after them.
   *
   * Not back into `4 Confirming`: by the time either is on screen the booking exists, and stepping
   * back into the screen that was about making it would be stepping into a question already
   * answered. Not `5b` back into `5 Finding` either — that search has ended.
   */
  finding: "where-to",
  "no-driver": "where-to",
  "driver-coming": "where-to",
  "in-trip": "driver-coming",
  complete: "where-to",
  "fare-differs": "complete",

  /* The commerce chain, in the design file's own screen order. */
  products: "merchants",
  cart: "products",
  "cart-conflict": "cart",
  checkout: "cart",
  "order-tracking": "merchants",

  /*
   * The stop plan, reached from somewhere this application does not draw.
   *
   * `where-to` rather than `multi-stop`, which would be the design file's own order: that screen is
   * blocked on its fare and its dwell, and `tests/navigation.test.ts` asserts a back destination is
   * a screen a rider can actually get to. So it leads to the front door, the way `refund` and
   * `8 Complete` do, until the screen before it exists.
   */
  "multi-stop-in-trip": "where-to",

  /* The money and account screens, each reached from somewhere this application does not draw. */
  refund: "where-to",
  /*
   * The history, which the design reaches from an account menu it does not draw.
   *
   * `settings` rather than `where-to`: `14 WP13` groups the money and account screens together
   * and `consent` and `payment-methods` already step back into the settings screen, which is the
   * only account surface this application draws. A history is an account screen in the same
   * sense, so it goes back where its two neighbours do rather than to the front door.
   */
  "order-history": "settings",
  consent: "settings",
  settings: "where-to",
  /*
   * The wallet and the methods list, which the design reaches from an account menu it does not draw.
   *
   * `14 WP13`'s app bar carries `arrow_back` on all three and the screen switcher above them is the
   * design file's own furniture rather than the product's, so nothing in the design says where back
   * is. `where-to` is the front door, which is the same answer `refund` and the payment-outcome
   * screens take and the one `tests/navigation.test.ts` requires: a destination a rider can reach.
   *
   * **`wallet-not-authoritative` goes back to the front door and not to `wallet`**, which would have
   * been the obvious reading of two screens of one thing. They are two states of one screen rather
   * than two steps, and `Wallet.tsx` corrects the address between them with `replaceState` — so a
   * back that led from one to the other would lead into a screen the data would immediately correct
   * away from, which is the loop `Quote`'s own entry avoids for the same reason.
   */
  "payment-methods": "settings",
  wallet: "where-to",
  "wallet-not-authoritative": "where-to",

  /* The payment outcome, which is about a ride rather than about the screen before it. */
  "approve-on-phone": "where-to",
  "outcome-unknown": "where-to",
  "still-unknown": "where-to",
  "confirmed-paid": "where-to",
  "confirmed-not-charged": "where-to",
};
